import { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { Heading, Button, Columns, Content, Modal } from 'react-bulma-components';
import { navigate } from '@reach/router';
import QRCode from 'qrcode.react';
import TotpInput from '../../input/totpInput';
import { axios } from '../../../services/axios';
import { logService } from '../../../services/LogService';
import Validator from '../../../services/Validator';
import ContentLoader from 'react-content-loader';
import { profileService } from '../../../services/profileService';
import SecretCodeContainer from './secretCodeContainer';
import SimpleMessages from '../../common/simple-messages/simpleMessages';
import CustomSnackbarV2 from '../../v2/snackbar';
import { CommonContextV2 } from 'components/v2';

import './mfaPage.scss';
import { COMMON_ERROR_MESSAGE, errorMessageMap, ERRORS_CODE } from '../../../services/Constant';

import googlePlayBadge from '../../../assets/images/google-play-badge.png';
import appStorePlayBadge from '../../../assets/images/app-store-badge.svg';

const formatLockTime = (lockTimeMs) => {
    const hours = Math.floor(lockTimeMs / (1000 * 60 * 60));
    const minutes = Math.floor((lockTimeMs % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes };
};

const MfaPage = () => {
    const [setupStep, setSetupStep] = useState(0);
    const [totp, setTotp] = useState('');
    const [showSecret, setShowSecret] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingRecoveryCode, setLoadingRecoveryCode] = useState(false);
    const [errors, setErrors] = useState([]);
    const [disableTotpSuccess, setDisableTotpSuccess] = useState([]);
    const [isExternalIdpUser, setIsExternalIdpUser] = useState(false);
    const [isDisableTotpConfirmed, setIsDisableTotpConfirmed] = useState([]);
    const [isLoadingCheckDisableTotp, setIsLoadingCheckDisableTotp] = useState(false);

    const [mfaInfo, setMfaInfo] = useState();
    const mfaUpdate = useCallback((info) => {
        const userInfo = info['user'] || {};
        setIsExternalIdpUser(userInfo['isExternalIDPUser']);
        setMfaInfo(info['mfa']);
    }, []);

    const { query } = useContext(CommonContextV2);

    const [showModal, setShowModal] = useState();

    const callbackMfaUpdate = (updatedMfa) => profileService.updateInfo('mfa', updatedMfa);

    const onSetupClick = useMemo(() => () => {
        setLoading(true);
        setErrors([]);
        (async () => {
            let code, recoveryCodes;
            let error;
            try {
                const response = await axios.get('/mfa/totp/setup');
                if (response?.data) {
                    code = response.data.code;
                    recoveryCodes = response.data.recoveryCodes;
                }
            } catch (err) {
                logService.error(err);
                error = err;
            }

            if (!code) {
                setErrors([{
                    error,
                    messages: [COMMON_ERROR_MESSAGE, errorMessageMap(error?.response?.data?.error?.code)]
                }]);
            } else {
                callbackMfaUpdate({ ...mfaInfo, code, recoveryCodes });
            }
            setLoading(false);
        })();
    }, [mfaInfo]);

    const onSubmit = useMemo(() => (totp) => {
        setLoading(true);
        setErrors([]);
        (async () => {
            try {
                const response = await axios.post('/mfa/totp/verifyToken', { code: totp });
                if (response?.data) {
                    callbackMfaUpdate({ ...mfaInfo, preferred: 'TOTP' });
                    // Refresh info
                    profileService.removeInfo();
                    await profileService.loadInfo();
                } else {
                    setErrors([{ messages: ['Your TOTP is invalid.'] }]);
                }
            } catch (err) {
                if (err?.response?.data?.error?.code === ERRORS_CODE.enableSoftwareTokenMFAException.code) {
                    setErrors([{ messages: ['Your TOTP is invalid.'] }]);
                } else {
                    setErrors([{ error: err, messages: [COMMON_ERROR_MESSAGE, errorMessageMap(err?.response?.data?.error?.code)] }]);
                }
                logService.error(err);
            }
            setLoading(false);
        })();
    }, [mfaInfo]);

    const onDisableClick = useMemo(() => () => {
        setLoading(true);
        setErrors([]);
        setDisableTotpSuccess([]);
        (async () => {
            let success, error;
            try {
                const response = await axios.post('/mfa/totp/disable');
                if (response?.data?.success) {
                    success = response.data;
                }
            } catch (err) {
                logService.error(err);
                error = err;
            }

            if (!success) {
                const errorData = error?.response?.data?.error;
                const isRateLimitError = errorData?.code === ERRORS_CODE.rateLimitError.code;
                if (isRateLimitError) {
                    const lockTime = errorData?.lockTime || 0;
                    const { hours, minutes } = formatLockTime(lockTime);
                    const errorMessage = `You've reached the daily limit for disabling multi-factor authentication. Please try again in ${hours} hours ${minutes} minutes`;
                    setErrors([{ messages: [errorMessage] }]);
                } else {
                    setErrors([{ error, messages: [COMMON_ERROR_MESSAGE, errorMessageMap(errorData?.code)] }]);
                }
            } else {
                setSetupStep(0);
                setTotp('');
                setShowSecret(false);

                // Display success message
                const successMessage = success.message || 'Confirmation email sent!';
                setDisableTotpSuccess([{ messages: [successMessage] }]);

                // Remove success message after 5 seconds
                setTimeout(() => {
                    setDisableTotpSuccess([]);
                }, 5000);
            }
            setLoading(false);
        })();
    }, [mfaInfo]);

    const mfaRecoveryCodeDom = useMemo(() => <div className='mfaOutlineBox'>
        <Heading subtitle renderAs={'h3'} size={5}>Your recovery codes</Heading>
        <SecretCodeContainer codes={mfaInfo?.recoveryCodes || []} buttonText={'Download codes'} actionText={'Downloaded!'} isDownload={true}/>
        <p>
            Please save your recovery codes in a safe place that you can easily access, in the event you lost your TOTP-generating app.
            Recovery code is a backup method allows you to log in to your account in that case (Your MFA will be deactivated).
        </p>
    </div>, [mfaInfo]);

    const mfaSetupDom = useMemo(() => {
        if (!mfaInfo?.code) {
            return <>
                <Heading subtitle renderAs={'h2'} size={6} className={mfaInfo?.isMfaForced ? 'required' : ''}>
                    {mfaInfo?.isMfaForced ? 'Your domain administrator has made MFA authentication setup mandatory for your account. Please configure MFA now.' : 'Your MFA hasn\'t been activated yet.'}
                </Heading>
                <div className='mfaOutlineBox'>
                    <Heading subtitle renderAs={'h3'} size={5}>How does MFA work?</Heading>
                    <p>
                        You are challenged to complete authentication using a time-based one-time (TOTP) password
                        after your user name and password have been verified.
                        The UI shows a second page for you to enter the TOTP password after you submit your user name and password.
                    </p>
                    <Heading subtitle renderAs={'h3'} size={5}>If I lost my TOTP-generating app?</Heading>
                    <p>
                        Before setting up your TOTP, you receive 3 set of recovery codes.
                        When you submit your user name and password, you will have an option to use your recovery code.
                        Your MFA will be disabled after successfully verifying your code.
                    </p>
                </div>
                <SimpleMessages messageList={errors}/>
                {isDisableTotpConfirmed.length > 0 && <CustomSnackbarV2 messageList={isDisableTotpConfirmed} type='success'/>}
                <div>
                    <Button onClick={onSetupClick} color='info' disabled={isExternalIdpUser || loading} loading={loading}>Setup TOTP</Button>
                    {isExternalIdpUser && (<p>
                        Because you sign in through your organization's Identity Provider (IdP), MFA setting in My OPSWAT Portal doesn't apply to your account. Please contact your IT team to update your MFA in your organization's IdP.
                    </p>)}
                </div>
            </>;
        }

        const str = 'otpauth://totp/' + mfaInfo.email + '?secret=' + mfaInfo.code + '&issuer=OPSWAT.SSO';
        if (setupStep === 0) {
            return <>
                <Heading subtitle renderAs={'h2'} size={6}>Step 1: Save your recovery codes (you can skip this step, your codes are also available after setting up your TOTP).</Heading>
                {mfaRecoveryCodeDom}
                <div className='qrCodeContainer'>
                    <Button color='info' onClick={() => setSetupStep(1)}>Next step</Button>
                </div>
            </>;
        } else if (setupStep === 1) {
            return <>
                <Heading subtitle renderAs={'h2'} size={6}>Step 2: Scan the below QR code by using any TOTP-generating app or enter <a href='/' onClick={(e) => {
                    e.preventDefault();
                    setShowSecret(!showSecret);
                }}>this text code</a> instead.</Heading>
                <div className='qrCodeContainer'>
                    {showSecret && <SecretCodeContainer codes={[mfaInfo.code]}/>}
                    <Columns className='qrSetup' breakpoint='desktop' variableGap={{ mobile: 0, desktop: 8 }}>
                        <Columns.Column>
                            <QRCode fgColor='#3D4A68' size={160} value={str}/>
                        </Columns.Column>
                        <Columns.Column>
                            <p className='getApp'>Get Google Authenticator App</p>
                            <a href='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2' target='_blank' rel='noopener noreferrer'>
                                <img src={googlePlayBadge} alt='Get on Google play'/>
                            </a>
                            <a href='https://apps.apple.com/us/app/google-authenticator/id388497605?itsct=apps_box&itscg=30200' target='_blank' rel='noopener noreferrer'>
                                <img className='appStore' src={appStorePlayBadge} alt='Get on Google play'/>
                            </a>
                        </Columns.Column>
                    </Columns>
                    <Button onClick={() => setSetupStep(0)}>Back</Button>
                    <Button color='info' onClick={() => setSetupStep(2)}>Next step</Button>
                </div>
            </>;
        }
        return <>
            <Heading subtitle renderAs={'h2'} size={6}>Step 3: Input your TOTP</Heading>
            <SimpleMessages messageList={errors}/>
            <div className='qrCodeContainer'>
                <TotpInput
                    value={totp}
                    onChange={(evt) => {
                        const newTotp = evt.target.value.replace(/[\s_]/g, '');
                        setTotp(newTotp);
                        if (Validator.isValidTotp(newTotp) && newTotp !== totp) {
                            onSubmit(newTotp);
                        }
                    }}
                    disabled={loading}
                />
                <Button onClick={() => setSetupStep(1)} disabled={loading}>Back</Button>
                <Button color='info' onClick={() => onSubmit(totp)} disabled={!Validator.isValidTotp(totp) || loading} loading={loading}>Submit</Button>
            </div>
        </>;


    }, [mfaInfo, onSetupClick, loading, mfaRecoveryCodeDom, errors, setupStep, showSecret, onSubmit, totp, isDisableTotpConfirmed]);

    const resetRecoveryCode = useMemo(() => () => {
        setLoadingRecoveryCode(true);
        (async () => {
            let recoveryCodes, error;
            try {
                const response = await axios.get('/mfa/totp/reset-recovery');
                recoveryCodes = response?.data?.recoveryCodes;
            } catch (err) {
                logService.error(err);
                error = err;
            }

            if (!recoveryCodes) {
                setErrors([{ error, messages: [COMMON_ERROR_MESSAGE, errorMessageMap(error?.response?.data?.error?.code)] }]);
            } else {
                callbackMfaUpdate({ ...mfaInfo, recoveryCodes });
            }
            setLoadingRecoveryCode(false);
        })();
    }, [mfaInfo]);

    const mfaPreferredDom = useMemo(() => {
        return <>
            <Heading subtitle renderAs={'h2'} size={6}>Your MFA is activated.</Heading>
            {mfaRecoveryCodeDom}
            <SimpleMessages messageList={errors}/>
            {disableTotpSuccess.length > 0 && <CustomSnackbarV2 messageList={disableTotpSuccess} type='success'/>}
            <div>
                <Button
                    onClick={() => setShowModal({
                        action: onDisableClick,
                        content: 'Are you sure you want to disable your MFA?',
                        color: 'danger'
                    })}
                    color='danger'
                    disabled={loading || loadingRecoveryCode}
                    loading={loading}
                >
                    Disable MFA
                </Button>
                <Button
                    onClick={() => setShowModal({
                        action: () => resetRecoveryCode(),
                        content: 'Are you sure you want to reset your current recovery codes?',
                        color: 'info'
                    })}
                    color='info'
                    disabled={loading || loadingRecoveryCode}
                    loading={loadingRecoveryCode}
                >
                    {!mfaInfo?.recoveryCodes ? 'Show Recovery Code' : 'Reset Recovery Code'}
                </Button>
            </div>
        </>;
    }, [mfaInfo, onDisableClick, mfaRecoveryCodeDom, errors, disableTotpSuccess, loading, loadingRecoveryCode, resetRecoveryCode]);

    const dom = useMemo(() => {
        if (!mfaInfo || isLoadingCheckDisableTotp) {
            return <ContentLoader
                height={150}
                width='100%'
                speed={1}
            >
                <rect x="0" y="10" rx="10" ry="10" width={`${20 + Math.random() * 80}%`} height="20"/>
                <rect x="0" y="50" rx="10" ry="10" width={`${20 + Math.random() * 80}%`} height="20"/>
                <rect x="0" y="90" rx="10" ry="10" width={`${20 + Math.random() * 80}%`} height="20"/>
                <rect x="0" y="130" rx="10" ry="10" width={`${20 + Math.random() * 80}%`} height="20"/>
            </ContentLoader>;
        }

        if (!mfaInfo.preferred) {
            // If no MFA setting yet
            return mfaSetupDom;
        }
        return mfaPreferredDom;


    }, [mfaInfo, mfaSetupDom, mfaPreferredDom, isLoadingCheckDisableTotp]);

    const modalConfirm = useMemo(() => {
        return <Modal className='mfaModal' show={!!showModal} closeOnBlur={true} onClose={() => setShowModal(undefined)}>
            <Content>
                <Modal.Card>
                    <Modal.Card.Head onClose={() => setShowModal(undefined)}>
                        <Modal.Card.Title>
                        </Modal.Card.Title>
                    </Modal.Card.Head>
                    <Modal.Card.Body>
                        <Content>
                            {showModal?.content}
                        </Content>
                    </Modal.Card.Body>
                    <Modal.Card.Foot>
                        <Button onClick={() => {
                            showModal.action();
                            setShowModal(undefined);
                        }} color={showModal?.color}>
                            Yes
                        </Button>
                        <Button onClick={() => setShowModal(undefined)}>
                            No
                        </Button>
                    </Modal.Card.Foot>
                </Modal.Card>
            </Content>
        </Modal>;
    }, [showModal]);


    useEffect(() => {
        const token = query?.disable_totp_tk;
        if (!token) {
            return;
        }

        const confirmDisableMfa = async () => {
            try {
                setIsLoadingCheckDisableTotp(true);
                const response = await axios.post('/mfa/totp/disable/confirm', { totpDisableTk: token });
                if (response.status === 200 && response.data?.success) {
                    const successMessage = response.data.message || 'MFA has been disabled successfully';
                    setIsDisableTotpConfirmed([{ messages: [successMessage] }]);
                    profileService.updateInfo('mfa', { ...profileService.getInfo().mfa, preferred: '', code: '', recoveryCodes: undefined  });
                    mfaUpdate(profileService.getInfo());
                    await profileService.loadInfo();
                    // Remove success message after 5 seconds
                    setTimeout(() => {
                        setIsDisableTotpConfirmed([]);
                    }, 5000);
                }
            } catch (err) {
                logService.error('Failed to disable MFA:', err);
                setErrors([{ error: err, messages: [COMMON_ERROR_MESSAGE, err?.response?.data?.error?.message] }]);
            } finally {
                setIsLoadingCheckDisableTotp(false);
                navigate(window.location.pathname, { replace: true });
            }
        };

        confirmDisableMfa();
    }, [query, profileService, mfaUpdate]);

    useEffect(() => {
        mfaUpdate(profileService.getInfo());
        profileService.registerUpdateEvent(mfaUpdate);
        return () => profileService.unregisterUpdateEvent(mfaUpdate);
    }, []);


    return <div className='mfaPage'>
        <Heading renderAs={'h1'} weight={'normal'}>Multi-factor authentication</Heading>
        {dom}
        {modalConfirm}
    </div>;
};

export default MfaPage;
